import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stage, Layer, Rect, Transformer } from 'react-konva';
import { ChevronLeft, ChevronRight, Edit, Eye, EyeOff, Trash2, Check, X, RotateCw } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '../../components/ui/button';
import ZonePicker from '../../components/damage/ZonePicker';
import { Textarea } from '../../components/ui/textarea';
import { dataApi } from '../../services/api';
import appwrite from '../../services/appwrite';

const parseJsonArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value || '[]');
    } catch (err) {
      console.error('[ImageLoad] Failed to parse JSON payload', err);
      return [];
    }
  }
  return [];
};

const normalizeTagsData = (value) => {
  const raw = parseJsonArray(value);
  return raw
    .map((tag, idx) => {
      if (typeof tag === 'string') {
        return { name: tag, confidence: 100 };
      }
      if (tag && typeof tag === 'object') {
        const name = tag.name || tag.label || tag.part || `Tag ${idx + 1}`;
        if (!name) return null;
        const confidenceValue = Number(tag.confidence);
        const confidence = Number.isFinite(confidenceValue) ? Math.round(confidenceValue) : 100;
        return {
          ...tag,
          name,
          confidence: Math.max(0, Math.min(100, confidence))
        };
      }
      return null;
    })
    .filter(Boolean);
};

const normalizeDamageBoxesData = (value) => {
  const raw = parseJsonArray(value);
  const toNumber = (input, fallback = 0) => {
    const num = Number(input);
    return Number.isFinite(num) ? num : fallback;
  };

  return raw
    .map((box, idx) => {
      if (!box || typeof box !== 'object') return null;
      const width = Math.max(0, toNumber(box.width));
      const height = Math.max(0, toNumber(box.height));
      return {
        id: box.id || `box-${idx}`,
        x: Math.round(toNumber(box.x)),
        y: Math.round(toNumber(box.y)),
        width: Math.round(width),
        height: Math.round(height),
        label: box.label || box.part || `damage-${idx + 1}`,
        confidence: Math.max(0, Math.min(100, Math.round(toNumber(box.confidence)))),
        units: box.units === 'percent' ? 'percent' : 'px'
      };
    })
    .filter((box) => box && box.width > 0 && box.height > 0);
};

const resolveImageUrl = (imageRecord) => {
  if (!imageRecord) return null;
  if (imageRecord.src) return imageRecord.src;
  if (imageRecord.url) return imageRecord.url;
  if (imageRecord.storagePath) {
    return dataApi.getImageUrl(imageRecord.storagePath);
  }
  return null;
};

const normalizeRotation = (value = 0) => ((value % 360) + 360) % 360;

const getDimensionsForRotation = (naturalSize, rotation) => {
  const width = naturalSize?.width || 0;
  const height = naturalSize?.height || 0;
  if (!width || !height) {
    return { width: 0, height: 0 };
  }

  const normalized = normalizeRotation(rotation);
  const isVertical = normalized === 90 || normalized === 270;

  return {
    width: isVertical ? height : width,
    height: isVertical ? width : height
  };
};

const clampBoxToDimensions = (box, width, height) => {
  if (!width || !height) return box;
  const maxX = Math.max(0, width - box.width);
  const maxY = Math.max(0, height - box.height);

  return {
    ...box,
    x: Math.max(0, Math.min(maxX, box.x)),
    y: Math.max(0, Math.min(maxY, box.y))
  };
};

/**
 * DamageImageForm - Image viewer with damage bounding box support
 * Renamed from EnhancedImageViewer
 */
const DamageImageForm = ({
  images = [],
  initialIndex = 0,
  isOpen,
  onClose,
  onImageUpdate,
  quoteRequestId,
  insurerConfigId = null
}) => {
  // Current image state
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [renderedSize, setRenderedSize] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  
  // Edit mode state (for bounding boxes only)
  const [isEditMode, setIsEditMode] = useState(false);
  const [showBoxes, setShowBoxes] = useState(true);
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [newBox, setNewBox] = useState(null);
  
  // Image hover state for navigation buttons
  const [isImageHovered, setIsImageHovered] = useState(false);
  
  // Image rotation state (0, 90, 180, 270)
  const [rotation, setRotation] = useState(0);
  
  // Data state
  const [tags, setTags] = useState([]);
  const [damageBoxes, setDamageBoxes] = useState([]);
  const [notes, setNotes] = useState('');
  const [zone, setZone] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Refs
  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const lastLoadedImageKeyRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false); // Use ref to avoid stale closure issues
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const tagsRef = useRef(tags);
  const damageBoxesRef = useRef(damageBoxes);
  const notesRef = useRef(notes);
  const rotationRef = useRef(rotation);
  
  // Keep refs in sync with state
  useEffect(() => { tagsRef.current = tags; }, [tags]);
  useEffect(() => { damageBoxesRef.current = damageBoxes; }, [damageBoxes]);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { rotationRef.current = rotation; }, [rotation]);

  const currentImage = images[currentIndex];
  const currentImageRef = useRef(currentImage);
  const currentImageId = currentImage?.id || currentImage?.$id || null;
  const prevImageIdRef = useRef(currentImageId);

  useEffect(() => {
    currentImageRef.current = currentImage;
  }, [currentImage]);

  const hydrateImageState = useCallback((imageRecord, source = 'local', notifyParent = false) => {
    if (!imageRecord) return;

    const normalizedTags = normalizeTagsData(imageRecord.tags);
    const normalizedBoxes = normalizeDamageBoxesData(imageRecord.damageBoxes);

    console.log('[ImageLoad] Parsed data:', {
      source,
      tagsCount: normalizedTags.length,
      boxesCount: normalizedBoxes.length
    });

    setTags(normalizedTags);
    setDamageBoxes(normalizedBoxes);
    setNotes(imageRecord.notes || '');
    setZone(imageRecord.zone || '');
    setRotation(imageRecord.rotation || 0);
    setSelectedBoxId(null);

    if (notifyParent && onImageUpdate && (imageRecord.id || imageRecord.$id)) {
      const recordId = imageRecord.id || imageRecord.$id;
      onImageUpdate(recordId, {
        tags: normalizedTags,
        damageBoxes: normalizedBoxes,
        hasDamage: normalizedBoxes.length > 0,
        notes: imageRecord.notes || '',
        rotation: imageRecord.rotation || 0
      });
    }

    const resolvedUrl = resolveImageUrl(imageRecord);
    if (resolvedUrl) {
      setImageUrl(resolvedUrl);
    } else {
      setImageLoading(false);
    }
  }, [onImageUpdate]);

  // Auto-save function with debounce
  // Uses refs to get latest values, avoiding stale closure issues
  const triggerAutoSave = useCallback((updates = {}) => {
    if (!currentImageId) return;
    
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Store the updates in a ref so we can access them in the timeout
    const pendingUpdates = { ...updates };
    
    // Debounce save by 500ms
    saveTimeoutRef.current = setTimeout(async () => {
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        // Use refs to get the LATEST state values, use pending updates if provided
        const currentTags = pendingUpdates.tags !== undefined ? pendingUpdates.tags : tagsRef.current;
        const currentBoxes = pendingUpdates.damageBoxes !== undefined ? pendingUpdates.damageBoxes : damageBoxesRef.current;
        const currentNotes = pendingUpdates.notes !== undefined ? pendingUpdates.notes : notesRef.current;
        const currentRotation = pendingUpdates.rotation !== undefined ? pendingUpdates.rotation : rotationRef.current;
        
        console.log('[AutoSave] Saving:', { 
          tagsCount: currentTags.length, 
          boxesCount: currentBoxes.length,
          notesLength: currentNotes?.length || 0,
          rotation: currentRotation
        });
        
        const saveData = {
          tags: JSON.stringify(currentTags),
          damageBoxes: JSON.stringify(currentBoxes),
          hasDamage: currentBoxes.length > 0,
          notes: currentNotes,
          rotation: currentRotation
        };
        
        await dataApi.updateQuoteRequestImage(currentImageId, saveData);
        
        if (onImageUpdate) {
          onImageUpdate(currentImageId, { 
            tags: currentTags, 
            damageBoxes: currentBoxes, 
            hasDamage: saveData.hasDamage, 
            notes: currentNotes,
            rotation: currentRotation
          });
        }
        
        console.log('[AutoSave] Save complete');
      } catch (err) {
        console.error('[AutoSave] Failed to save:', err);
      } finally {
        // Delay resetting isSaving to prevent realtime updates from overwriting
        setTimeout(() => {
          isSavingRef.current = false;
          setIsSaving(false);
        }, 1000); // Increased delay to 1 second
      }
    }, 500);
    // Note: We intentionally don't include tags, damageBoxes, notes as dependencies
    // because we read their values from refs inside the timeout callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageId, onImageUpdate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Start at the clicked image each time the viewer opens, and when a different
  // image is clicked. Opening the same index again — of a different list, say a
  // section's photos after the whole grid — must still land on it, not on
  // wherever the last visit navigated to.
  // Don't reset when images array changes (e.g., from updates) as user may have navigated
  useEffect(() => {
    if (isOpen) setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  // Load image data when current image ID changes (not on every re-render)
  useEffect(() => {
    if (!isOpen || !currentImageId) return;

    const loadKey = `${currentImageId}:${currentIndex}`;
    if (lastLoadedImageKeyRef.current === loadKey) {
      return;
    }
    lastLoadedImageKeyRef.current = loadKey;

    console.log('[ImageLoad] Loading new image:', currentImageId, '(prev:', prevImageIdRef.current, ')');
    prevImageIdRef.current = currentImageId;

    let isMounted = true;

    setImageLoading(true);
    setImageUrl(null);
    setRenderedSize({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
    setNaturalSize({ width: 0, height: 0 });

    const initialRecord = currentImageRef.current;
    const initialId = initialRecord?.id || initialRecord?.$id;
    if (initialRecord && initialId === currentImageId) {
      hydrateImageState(initialRecord, 'initial');
    }

    const fetchLatest = async () => {
      try {
        const latest = await dataApi.quoteRequestImageGet(currentImageId);
        if (isMounted && latest) {
          hydrateImageState(latest, 'api');
        }
      } catch (err) {
        if (isMounted) {
          console.error('[ImageLoad] Failed to fetch latest image data:', err);
        }
      }
    };

    fetchLatest();

    return () => {
      isMounted = false;
    };
  }, [currentImageId, hydrateImageState, isOpen, currentIndex]);

  useEffect(() => {
    if (!isOpen) {
      lastLoadedImageKeyRef.current = null;
    }
  }, [isOpen]);

  // Subscribe to realtime updates for the current image
  useEffect(() => {
    if (!currentImageId) return;

    console.log('[Realtime] Subscribing to document:', currentImageId);
    
    const unsubscribe = appwrite.subscribeToDocument(
      'quote_request_images',
      currentImageId,
      (response) => {
        console.log('[Realtime] Update received, isSaving:', isSavingRef.current);
        
        // Use ref to check saving state (avoids stale closure)
        if (response.payload && !isSavingRef.current) {
          const payload = response.payload;
          const normalizedTags = normalizeTagsData(payload.tags);
          const normalizedBoxes = normalizeDamageBoxesData(payload.damageBoxes);

          console.log('[Realtime] Applying update:', { 
            tagsCount: normalizedTags.length, 
            boxesCount: normalizedBoxes.length,
            rotation: payload.rotation 
          });
          
          setTags(normalizedTags);
          setDamageBoxes(normalizedBoxes);
          setNotes(payload.notes || '');
          setSelectedBoxId(null);
          if (payload.rotation !== undefined) {
            setRotation(payload.rotation);
          }

          if (onImageUpdate) {
            onImageUpdate(currentImageId, {
              tags: normalizedTags,
              damageBoxes: normalizedBoxes,
              hasDamage: normalizedBoxes.length > 0,
              notes: payload.notes || '',
              rotation: payload.rotation || 0
            });
          }
        } else {
          console.log('[Realtime] Ignoring update (currently saving)');
        }
      }
    );

    return () => {
      console.log('[Realtime] Unsubscribing from document:', currentImageId);
      unsubscribe && unsubscribe();
    };
  }, [currentImageId, onImageUpdate]);

  // Navigation callbacks - with cycling
  const navigatePrev = useCallback(() => {
    // Disable edit mode when navigating
    setIsEditMode(false);
    setSelectedBoxId(null);
    
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      // Cycle to last image
      setCurrentIndex(images.length - 1);
    }
  }, [currentIndex, images.length]);

  const navigateNext = useCallback(() => {
    // Disable edit mode when navigating
    setIsEditMode(false);
    setSelectedBoxId(null);
    
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Cycle to first image
      setCurrentIndex(0);
    }
  }, [currentIndex, images.length]);

  // Delete box callback with auto-save
  const deleteSelectedBox = useCallback(() => {
    if (!selectedBoxId) return;
    setDamageBoxes(prev => {
      const newBoxes = prev.filter(box => box.id !== selectedBoxId);
      triggerAutoSave({ damageBoxes: newBoxes });
      return newBoxes;
    });
    setSelectedBoxId(null);
  }, [selectedBoxId, triggerAutoSave]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowLeft') {
        navigatePrev();
      } else if (e.key === 'ArrowRight') {
        navigateNext();
      } else if (e.key === 'Escape') {
        if (isEditMode) {
          setIsEditMode(false);
          setSelectedBoxId(null);
        } else {
          onClose();
        }
      } else if (e.key === 'Delete' && selectedBoxId && isEditMode) {
        deleteSelectedBox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isEditMode, selectedBoxId, navigatePrev, navigateNext, onClose, deleteSelectedBox]);

  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && stageRef.current) {
      const stage = stageRef.current;
      const selectedNode = stage.findOne(`#${selectedBoxId}`);
      
      if (selectedNode && isEditMode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef.current.getLayer().batchDraw();
      } else {
        transformerRef.current.nodes([]);
      }
    }
  }, [selectedBoxId, isEditMode]);

  // Convert stored box (in pixels) to rendered stage coordinates
  const boxToPixels = useCallback((box) => {
    if (renderedSize.width === 0 || renderedSize.height === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    // Legacy records still in percentages fall back to old logic
    if (box.units === 'percent') {
      return {
        x: (box.x / 100) * renderedSize.width,
        y: (box.y / 100) * renderedSize.height,
        width: (box.width / 100) * renderedSize.width,
        height: (box.height / 100) * renderedSize.height
      };
    }

    const dims = getDimensionsForRotation(naturalSize, rotation);
    if (!dims.width || !dims.height) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    const scaleX = renderedSize.width / dims.width;
    const scaleY = renderedSize.height / dims.height;

    return {
      x: box.x * scaleX,
      y: box.y * scaleY,
      width: box.width * scaleX,
      height: box.height * scaleY
    };
  }, [renderedSize, naturalSize, rotation]);

  // Convert stage pixel coordinates back to stored pixel units
  const pixelsToBox = useCallback((rect) => {
    if (renderedSize.width === 0 || renderedSize.height === 0) {
      return { x: 0, y: 0, width: 0, height: 0, units: 'px' };
    }

    const dims = getDimensionsForRotation(naturalSize, rotation);
    if (!dims.width || !dims.height) {
      return { x: 0, y: 0, width: 0, height: 0, units: 'px' };
    }

    const scaleX = dims.width / renderedSize.width;
    const scaleY = dims.height / renderedSize.height;

    return {
      x: Math.round(rect.x * scaleX),
      y: Math.round(rect.y * scaleY),
      width: Math.round(rect.width * scaleX),
      height: Math.round(rect.height * scaleY),
      units: 'px'
    };
  }, [renderedSize, naturalSize, rotation]);

  // Convert legacy percentage boxes to pixel units once image dimensions are known
  useEffect(() => {
    if (!naturalSize.width || !naturalSize.height) return;
    if (!damageBoxes.length) return;

    const hasLegacyBoxes = damageBoxes.some(box => box.units === 'percent');
    if (!hasLegacyBoxes) return;

    const dims = getDimensionsForRotation(naturalSize, rotation);
    if (!dims.width || !dims.height) return;

    const converted = damageBoxes.map(box => {
      if (box.units !== 'percent') return box;
      return {
        ...box,
        x: Math.round((box.x / 100) * dims.width),
        y: Math.round((box.y / 100) * dims.height),
        width: Math.round((box.width / 100) * dims.width),
        height: Math.round((box.height / 100) * dims.height),
        units: 'px'
      };
    });

    setDamageBoxes(converted);
    triggerAutoSave({ damageBoxes: converted });
  }, [naturalSize, rotation, damageBoxes, triggerAutoSave]);

  // Clamp a value to the image bounds
  const clampToImage = useCallback((x, y, width = 0, height = 0) => {
    const clampedX = Math.max(0, Math.min(x, renderedSize.width - width));
    const clampedY = Math.max(0, Math.min(y, renderedSize.height - height));
    return { x: clampedX, y: clampedY };
  }, [renderedSize]);

  const handleStageMouseDown = (e) => {
    if (!isEditMode) return;
    
    // Stage click (not on a box)
    const clickedOnEmpty = e.target === e.target.getStage();
    
    if (clickedOnEmpty) {
      setSelectedBoxId(null);
      
      const pos = e.target.getStage().getPointerPosition();
      
      // Only start drawing if within image bounds
      if (pos.x >= 0 && pos.x <= renderedSize.width && 
          pos.y >= 0 && pos.y <= renderedSize.height) {
        setIsDrawing(true);
        setNewBox({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0
        });
      }
    }
  };

  const handleStageMouseMove = (e) => {
    if (!isDrawing || !newBox) return;
    
    const pos = e.target.getStage().getPointerPosition();
    
    // Clamp to image bounds
    const clampedX = Math.max(0, Math.min(pos.x, renderedSize.width));
    const clampedY = Math.max(0, Math.min(pos.y, renderedSize.height));
    
    setNewBox({
      ...newBox,
      width: clampedX - newBox.x,
      height: clampedY - newBox.y
    });
  };

  const handleStageMouseUp = () => {
    if (!isDrawing || !newBox) return;
    
    if (Math.abs(newBox.width) > 20 && Math.abs(newBox.height) > 20) {
      const normalized = {
        x: newBox.width < 0 ? newBox.x + newBox.width : newBox.x,
        y: newBox.height < 0 ? newBox.y + newBox.height : newBox.y,
        width: Math.abs(newBox.width),
        height: Math.abs(newBox.height)
      };
      
      const percentBox = pixelsToBox(normalized);
      const newId = `box-${Date.now()}`;
      
      // Just store positional data
      const newBoxData = {
        id: newId,
        x: percentBox.x,
        y: percentBox.y,
        width: percentBox.width,
        height: percentBox.height,
        units: percentBox.units || 'px'
      };
      
      setDamageBoxes(prev => {
        const newBoxes = [...prev, newBoxData];
        triggerAutoSave({ damageBoxes: newBoxes });
        return newBoxes;
      });
      setSelectedBoxId(newId);
    }
    
    setIsDrawing(false);
    setNewBox(null);
  };

  const handleBoxClick = (boxId) => {
    if (isEditMode) {
      setSelectedBoxId(boxId);
    }
  };

  const handleBoxDragEnd = (boxId, e) => {
    const node = e.target;
    const width = node.width() * node.scaleX();
    const height = node.height() * node.scaleY();
    
    // Clamp position to image bounds
    const clamped = clampToImage(node.x(), node.y(), width, height);
    node.x(clamped.x);
    node.y(clamped.y);
    
    const percentBox = pixelsToBox({
      x: clamped.x,
      y: clamped.y,
      width: width,
      height: height
    });

    setDamageBoxes(prev => {
      const newBoxes = prev.map(box => box.id === boxId ? { 
        ...box, 
        x: percentBox.x,
        y: percentBox.y,
        width: percentBox.width,
        height: percentBox.height,
        units: percentBox.units || 'px'
      } : box);
      triggerAutoSave({ damageBoxes: newBoxes });
      return newBoxes;
    });
  };

  const handleBoxTransformEnd = (boxId, e) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    
    node.scaleX(1);
    node.scaleY(1);
    
    const width = node.width() * scaleX;
    const height = node.height() * scaleY;
    
    // Clamp to image bounds
    const clamped = clampToImage(node.x(), node.y(), width, height);
    node.x(clamped.x);
    node.y(clamped.y);
    
    const percentBox = pixelsToBox({
      x: clamped.x,
      y: clamped.y,
      width: width,
      height: height
    });

    setDamageBoxes(prev => {
      const newBoxes = prev.map(box => box.id === boxId ? { 
        ...box, 
        x: percentBox.x,
        y: percentBox.y,
        width: percentBox.width,
        height: percentBox.height,
        units: percentBox.units || 'px'
      } : box);
      triggerAutoSave({ damageBoxes: newBoxes });
      return newBoxes;
    });
  };

  const handleNotesChange = (e) => {
    const newNotes = e.target.value;
    setNotes(newNotes);
    triggerAutoSave({ notes: newNotes });
  };

  const handleZoneChange = async (newZone) => {
    setZone(newZone);
    if (!currentImageId) return;
    try {
      await dataApi.updateQuoteRequestImage(currentImageId, { zone: newZone });
    } catch (err) {
      console.error('[DamageImageForm] Failed to update zone:', err);
    }
  };

  const exitEditMode = () => {
    setIsEditMode(false);
    setSelectedBoxId(null);
  };

  // Rotate image 90 degrees clockwise and save to database
  const rotateImage = () => {
    if (!naturalSize.width || !naturalSize.height) return;

    const newRotation = (rotation + 90) % 360;
    const currentDims = getDimensionsForRotation(naturalSize, rotation);
    const nextDims = getDimensionsForRotation(naturalSize, newRotation);

    if (!currentDims.width || !currentDims.height || !nextDims.width || !nextDims.height) {
      return;
    }

    setRotation(newRotation);
    
    setDamageBoxes(prev => {
      const newBoxes = prev.map(box => {
        if (box.units === 'percent') {
          return box;
        }
        const rotated = {
          ...box,
          x: currentDims.height - box.y - box.height,
          y: box.x,
          width: box.height,
          height: box.width
        };
        return clampBoxToDimensions(rotated, nextDims.width, nextDims.height);
      });
      triggerAutoSave({ rotation: newRotation, damageBoxes: newBoxes });
      return newBoxes;
    });

    setTimeout(() => calculateRenderedSize(), 50);
  };

  const applyRenderedSize = useCallback((width, height) => {
    if (!width || !height) {
      return false;
    }

    let changed = false;
    setRenderedSize((prev) => {
      if (
        Math.round(prev.width) === Math.round(width) &&
        Math.round(prev.height) === Math.round(height)
      ) {
        return prev;
      }
      changed = true;
      return {
        width,
        height,
        offsetX: 0,
        offsetY: 0
      };
    });
    return changed;
  }, []);

  const calculateRenderedSize = useCallback(() => {
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();

      if (rect.width > 0 && rect.height > 0) {
        if (applyRenderedSize(rect.width, rect.height)) {
          console.log('[Sizing] Calculated rendered size:', {
            width: rect.width,
            height: rect.height,
            offsetX: 0,
            offsetY: 0
          });
        }
        return true;
      }
    }
    return false;
  }, [applyRenderedSize]);

  const attachResizeObserver = useCallback((node) => {
    if (!node) return;
    const hasResizeObserver =
      (typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined') ||
      typeof ResizeObserver !== 'undefined';
    if (!hasResizeObserver) return;

    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    const ObserverImpl =
      (typeof window !== 'undefined' && window.ResizeObserver) || ResizeObserver;
    const observer = new ObserverImpl((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const width = entry.contentRect?.width;
      const height = entry.contentRect?.height;
      if (width > 0 && height > 0) {
        applyRenderedSize(width, height);
      }
    });

    observer.observe(node);
    resizeObserverRef.current = observer;
  }, [applyRenderedSize]);

  // Handler for when the image loads - capture its rendered dimensions
  const handleImageLoad = (e) => {
    const img = e.target;
    imageRef.current = img; // Ensure ref is set
    
    console.log('[Image] Loaded, natural size:', img.naturalWidth, 'x', img.naturalHeight);
    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    attachResizeObserver(img);
    
    // Try to calculate size immediately
    if (!calculateRenderedSize()) {
      // If it fails, try again after a short delay (DOM may not be ready)
      setTimeout(calculateRenderedSize, 50);
    }
    
    setImageLoading(false);
  };

  const handleImageError = () => {
    console.error('Failed to load image:', currentImage?.src || currentImage?.url);
    setImageLoading(false);
  };

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!imageUrl) return;
    if (typeof window === 'undefined') return;

    let isCancelled = false;
    const preloadImg = new window.Image();
    preloadImg.onload = () => {
      if (isCancelled) return;
      if (preloadImg.naturalWidth && preloadImg.naturalHeight) {
        setNaturalSize((prev) => {
          if (
            prev.width === preloadImg.naturalWidth &&
            prev.height === preloadImg.naturalHeight
          ) {
            return prev;
          }
          return {
            width: preloadImg.naturalWidth,
            height: preloadImg.naturalHeight
          };
        });
      }
    };
    preloadImg.onerror = () => {
      if (isCancelled) return;
      console.warn('[Image] Failed to preload natural size for image URL', imageUrl);
    };
    preloadImg.src = imageUrl;

    return () => {
      isCancelled = true;
      preloadImg.onload = null;
      preloadImg.onerror = null;
    };
  }, [imageUrl]);

  // Update rendered size on window resize and when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    
    const updateRenderedSize = () => {
      calculateRenderedSize();
    };

    // Recalculate after dialog animation completes
    const timer = setTimeout(updateRenderedSize, 100);
    
    window.addEventListener('resize', updateRenderedSize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateRenderedSize);
    };
  }, [isOpen, calculateRenderedSize, currentIndex, rotation]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="max-w-[95vw] w-[95vw] h-[90vh] p-0 gap-0 overflow-hidden [&>button]:hidden flex"
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>Image Viewer</DialogTitle>
        </VisuallyHidden>
        {/* Main content - horizontal layout */}
        <div className="flex overflow-hidden flex-1">
          {/* Image area with navigation */}
          <div 
            className="relative flex items-center justify-center bg-neutral-900 flex-1"
            onMouseEnter={() => setIsImageHovered(true)}
            onMouseLeave={() => setIsImageHovered(false)}
          >
            {/* Previous button - only show on hover and when there are multiple images */}
            {images.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className={`absolute left-3 z-10 bg-black/30 hover:bg-black/50 text-white h-12 w-12 transition-opacity duration-200 ${isImageHovered ? 'opacity-100' : 'opacity-0'}`}
                onClick={navigatePrev}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
            )}

            {/* Canvas area - img with Konva overlay */}
            <div 
              ref={containerRef} 
              className="relative flex items-center justify-center w-full h-full"
            >
              {imageLoading && !imageUrl ? (
                <div className="text-white text-lg p-20">Loading...</div>
              ) : imageUrl ? (
                <>
                  {/* Regular img tag - constrained to fixed area */}
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt=""
                    className="block"
                    style={{
                      maxWidth: 'calc(100% - 40px)', // Account for padding
                      maxHeight: 'calc(100% - 40px)', // Account for padding
                      objectFit: 'contain',
                      transform: `rotate(${rotation}deg)`,
                      transition: 'transform 0.3s ease'
                    }}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    draggable={false}
                  />
                  
                  {/* Konva overlay for boxes - positioned exactly over the image */}
                  {renderedSize.width > 0 && (
                    <Stage
                      ref={stageRef}
                      width={renderedSize.width}
                      height={renderedSize.height}
                      onMouseDown={handleStageMouseDown}
                      onMouseMove={handleStageMouseMove}
                      onMouseUp={handleStageMouseUp}
                      style={{ 
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                        cursor: isEditMode ? 'crosshair' : 'default',
                        pointerEvents: isEditMode ? 'auto' : 'none',
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <Layer>
                        {/* Show boxes in edit mode always, otherwise respect showBoxes toggle */}
                        {(isEditMode || showBoxes) && damageBoxes.map((box) => {
                          const pixelBox = boxToPixels(box);
                          const isSelected = selectedBoxId === box.id;
                          
                          return (
                            <Rect
                              key={box.id}
                              id={box.id}
                              x={pixelBox.x}
                              y={pixelBox.y}
                              width={pixelBox.width}
                              height={pixelBox.height}
                              stroke={isSelected ? '#3b82f6' : '#ef4444'}
                              strokeWidth={isSelected ? 3 : 2}
                              fill={isSelected ? 'rgba(59, 130, 246, 0.1)' : 'rgba(239, 68, 68, 0.1)'}
                              draggable={isEditMode}
                              onClick={() => handleBoxClick(box.id)}
                              onTap={() => handleBoxClick(box.id)}
                              onDragEnd={(e) => handleBoxDragEnd(box.id, e)}
                              onTransformEnd={(e) => handleBoxTransformEnd(box.id, e)}
                            />
                          );
                        })}

                        {isDrawing && newBox && (
                          <Rect
                            x={newBox.width < 0 ? newBox.x + newBox.width : newBox.x}
                            y={newBox.height < 0 ? newBox.y + newBox.height : newBox.y}
                            width={Math.abs(newBox.width)}
                            height={Math.abs(newBox.height)}
                            stroke="#3b82f6"
                            strokeWidth={2}
                            dash={[5, 5]}
                            fill="rgba(59, 130, 246, 0.1)"
                          />
                        )}

                        {isEditMode && (
                          <Transformer
                            ref={transformerRef}
                            rotateEnabled={false}
                            boundBoxFunc={(oldBox, newBoxBound) => {
                              // Enforce minimum size
                              if (newBoxBound.width < 20 || newBoxBound.height < 20) {
                                return oldBox;
                              }
                              // Clamp to stage bounds
                              const clampedBox = { ...newBoxBound };
                              if (clampedBox.x < 0) clampedBox.x = 0;
                              if (clampedBox.y < 0) clampedBox.y = 0;
                              if (clampedBox.x + clampedBox.width > renderedSize.width) {
                                clampedBox.width = renderedSize.width - clampedBox.x;
                              }
                              if (clampedBox.y + clampedBox.height > renderedSize.height) {
                                clampedBox.height = renderedSize.height - clampedBox.y;
                              }
                              return clampedBox;
                            }}
                          />
                        )}
                      </Layer>
                    </Stage>
                  )}
                </>
              ) : (
                <div className="text-white text-lg">Failed to load image</div>
              )}
            </div>

            {/* Next button - only show on hover and when there are multiple images */}
            {images.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className={`absolute right-3 z-10 bg-black/30 hover:bg-black/50 text-white h-12 w-12 transition-opacity duration-200 ${isImageHovered ? 'opacity-100' : 'opacity-0'}`}
                onClick={navigateNext}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            )}
          </div>

          {/* Right panel */}
          <div className="w-80 border-l bg-background overflow-y-auto flex flex-col">
            {/* Panel header with controls */}
            <div className="p-3 border-b flex items-center justify-between gap-2 sticky top-0 bg-background z-10">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">Image {currentIndex + 1} of {images.length}</span>
                {isSaving && (
                  <span className="text-xs text-muted-foreground">Saving...</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {/* Rotate button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={rotateImage}
                  disabled={!naturalSize.width || !naturalSize.height}
                  title="Rotate image 90°"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
                
                {/* Show/hide only when not editing - boxes always visible in edit mode */}
                {!isEditMode && damageBoxes.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setShowBoxes(!showBoxes)}
                    title={showBoxes ? 'Hide boxes' : 'Show boxes'}
                  >
                    {showBoxes ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                )}
                {!isEditMode ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => setIsEditMode(true)}
                    title="Edit damage areas"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Boxes
                  </Button>
                ) : (
                  <>
                    {selectedBoxId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={deleteSelectedBox}
                        title="Delete selected box"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={exitEditMode}
                      title="Done editing boxes"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Done
                    </Button>
                  </>
                )}
                {/* Close button - moved to right of edit button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onClose}
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="p-4 flex-1 space-y-6">
              {/* Section selector */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                  Section
                </h4>
                <ZonePicker value={zone} onChange={handleZoneChange} />
              </div>

              {/* Notes section - always editable */}
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">
                  Notes
                </h4>
                <Textarea
                  value={notes}
                  onChange={handleNotesChange}
                  placeholder="Add notes about this image..."
                  className="min-h-[100px] text-sm"
                />
              </div>

              {/* Edit mode instructions */}
              {isEditMode && (
                <div className="p-3 bg-muted rounded-lg">
                  <h4 className="text-sm font-medium mb-2">Drawing Boxes</h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Click and drag to draw new boxes</li>
                    <li>• Click a box to select it</li>
                    <li>• Drag corners to resize</li>
                    <li>• Press Delete to remove</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DamageImageForm;
